import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Search,
  Bell,
  User,
  LogOut,
  Settings,
  Crown,
  Menu,
  TrendingUp,
  TrendingDown,
  AlertCircle,
} from 'lucide-react';
import { useAuthStore, useSidebarStore } from '../../store';
import {
  getUserNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  type UserNotification,
} from '../../utils/api';
import { getSettingsPath } from '../../utils/paths';
import { formatTierName } from '../../utils/format';

export function Header() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();
  const { isExpanded, openMobile } = useSidebarStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoadingNotifications(true);
      const data = await getUserNotifications(false, 10);
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.warn('Failed to fetch notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchNotifications();
    // Refresh notifications every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkRead = async (notificationId: number) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.warn('Failed to mark notification read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.warn('Failed to mark all notifications read:', error);
    }
  };

  const getNotificationIcon = (notification: UserNotification) => {
    if (notification.signal?.includes('BUY')) {
      return <TrendingUp className="h-4 w-4 text-emerald-500" />;
    }
    if (notification.signal?.includes('SELL')) {
      return <TrendingDown className="h-4 w-4 text-red-500" />;
    }
    return <AlertCircle className="h-4 w-4 text-blue-500" />;
  };

  const formatTimeAgo = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/analysis?ticker=${searchQuery.toUpperCase()}`);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  };

  return (
    <header
      className={clsx(
        'fixed top-0 right-0 z-30 h-16 transition-all duration-300',
        'bg-[var(--color-bg-primary)] border-b border-[var(--color-border)]',
        // On mobile, full width
        'left-0',
        // On desktop, offset by sidebar
        isExpanded ? 'md:left-52' : 'md:left-20'
      )}
    >
      <div className="flex h-full items-center justify-between px-4 md:px-6">
        {/* Mobile menu button */}
        <button
          onClick={openMobile}
          className={clsx(
            'md:hidden flex h-10 w-10 items-center justify-center rounded-lg mr-2 cursor-pointer',
            'bg-[var(--color-bg-secondary)] border border-[var(--color-border)]',
            'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
            'hover:bg-[var(--color-bg-tertiary)] transition-all'
          )}
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex-1 max-w-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search ticker (e.g., AAPL, TSLA)..."
              className={clsx(
                'w-full h-10 pl-10 pr-4 rounded-lg',
                'bg-[var(--color-bg-secondary)] border border-[var(--color-border)]',
                'text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]',
                'focus:outline-none focus:ring-2 focus:ring-[var(--accent-green)]/30 focus:border-[var(--color-accent)]',
                'transition-all'
              )}
            />
          </div>
        </form>

        {/* Right Side Actions */}
        <div className="flex items-center gap-2 md:gap-3 ml-3 md:ml-6">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => {
                setShowNotifications(!showNotifications);
                setShowUserMenu(false);
              }}
              className={clsx(
                'relative flex h-10 w-10 items-center justify-center rounded-lg cursor-pointer',
                'bg-[var(--color-bg-secondary)] border border-[var(--color-border)]',
                'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                'hover:bg-[var(--color-bg-tertiary)] transition-all'
              )}
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[var(--accent-red)] text-[10px] font-bold text-white flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div
                className={clsx(
                  'absolute right-0 top-12 w-80 rounded-lg',
                  'bg-[var(--color-bg-primary)] border border-[var(--color-border)]',
                  'shadow-lg animate-[fadeIn_0.15s_ease-out] z-50'
                )}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
                  <h3 className="font-semibold text-[var(--color-text-primary)]">
                    Notifications
                  </h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="text-xs text-blue-500 hover:text-blue-600 cursor-pointer"
                    >
                      Mark all read
                    </button>
                  )}
                </div>

                {/* Notifications List */}
                <div className="max-h-96 overflow-y-auto">
                  {loadingNotifications ? (
                    <div className="p-8 text-center text-[var(--color-text-muted)]">
                      Loading...
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="p-8 text-center">
                      <Bell className="h-8 w-8 text-[var(--color-text-muted)] mx-auto mb-2" />
                      <p className="text-sm text-[var(--color-text-muted)]">
                        No notifications yet
                      </p>
                      <p className="text-xs text-[var(--color-text-muted)] mt-1">
                        Set up alerts to get notified
                      </p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={clsx(
                          'flex items-start gap-3 px-4 py-3 border-b border-[var(--color-border)]',
                          'hover:bg-[var(--color-bg-secondary)] cursor-pointer transition-colors',
                          !notification.is_read && 'bg-blue-500/5'
                        )}
                        onClick={() => {
                          if (!notification.is_read) {
                            handleMarkRead(notification.id);
                          }
                          if (notification.ticker) {
                            navigate(`/analysis?ticker=${notification.ticker}`);
                            setShowNotifications(false);
                          }
                        }}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p
                              className={clsx(
                                'text-sm',
                                notification.is_read
                                  ? 'text-[var(--color-text-secondary)]'
                                  : 'text-[var(--color-text-primary)] font-medium'
                              )}
                            >
                              {notification.title}
                            </p>
                            {!notification.is_read && (
                              <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                            )}
                          </div>
                          {notification.message && (
                            <p className="text-xs text-[var(--color-text-muted)] mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                          )}
                          <p className="text-xs text-[var(--color-text-muted)] mt-1">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Footer */}
                {notifications.length > 0 && (
                  <div className="p-3 border-t border-[var(--color-border)]">
                    <button
                      onClick={() => {
                        navigate(getSettingsPath('alerts'));
                        setShowNotifications(false);
                      }}
                      className="w-full text-center text-sm text-blue-500 hover:text-blue-600 cursor-pointer"
                    >
                      Manage Alerts
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Menu */}
          {isAuthenticated ? (
            <div className="relative">
              <button
                onClick={() => {
                  setShowUserMenu(!showUserMenu);
                  setShowNotifications(false);
                }}
                className={clsx(
                  'flex items-center gap-3 h-10 pl-3 pr-4 rounded-lg cursor-pointer',
                  'bg-[var(--color-bg-secondary)] border border-[var(--color-border)]',
                  'hover:bg-[var(--color-bg-tertiary)] transition-all'
                )}
              >
                <div className="h-7 w-7 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--accent-green), var(--accent-blue))' }}>
                  <span className="text-xs font-bold text-white">
                    {user?.name?.[0] || user?.email?.[0] || 'U'}
                  </span>
                </div>
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-medium text-[var(--color-text-primary)]">
                    {user?.name || user?.email?.split('@')[0]}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-[var(--color-text-muted)]">
                    {user?.subscription_tier === 'pro' && (
                      <Crown className="h-3 w-3 text-yellow-500" />
                    )}
                    {formatTierName(user?.subscription_tier || 'free')}
                  </div>
                </div>
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div
                  className={clsx(
                    'absolute right-0 top-12 w-56 rounded-lg py-2',
                    'bg-[var(--color-bg-primary)] border border-[var(--color-border)]',
                    'shadow-lg animate-[fadeIn_0.15s_ease-out]'
                  )}
                >
                  <div className="px-4 py-2 border-b border-[var(--color-border)]">
                    <div className="text-sm font-medium text-[var(--color-text-primary)]">
                      {user?.name || 'User'}
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)]">
                      {user?.email}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      navigate(getSettingsPath());
                      setShowUserMenu(false);
                    }}
                    className={clsx(
                      'flex w-full items-center gap-3 px-4 py-2 cursor-pointer',
                      'text-sm text-[var(--color-text-secondary)]',
                      'hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]'
                    )}
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                  </button>

                  <button
                    onClick={() => {
                      navigate(getSettingsPath('subscription'));
                      setShowUserMenu(false);
                    }}
                    className={clsx(
                      'flex w-full items-center gap-3 px-4 py-2 cursor-pointer',
                      'text-sm text-[var(--color-text-secondary)]',
                      'hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]'
                    )}
                  >
                    <Crown className="h-4 w-4" />
                    Upgrade Plan
                  </button>

                  <div className="border-t border-[var(--color-border)] mt-2 pt-2">
                    <button
                      onClick={handleLogout}
                      className={clsx(
                        'flex w-full items-center gap-3 px-4 py-2 cursor-pointer',
                        'text-sm text-red-500',
                        'hover:bg-red-500/10'
                      )}
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="btn btn-primary flex items-center gap-2 h-10 px-4"
            >
              <User className="h-4 w-4" />
              Sign In
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
